import { z } from 'zod';
import { baseObjectSchema } from '../baseSchemas';
import { userSchema } from '../user/getUsers';
import { RecordMode } from '@/types/Base';
import { alterCode } from '../alterCodes/getAlterCodesWithConditions';

const materialTypeSchema = z.object({
  eType: z.number(),
  id: z.string(),
});

const companySchema = z.object({
  code: z.string(),
  id: z.string(),
  name: z.string(),
});

const vatCategorySchema = z.object({
  code: z.string(),
  id: z.string(),
  name: z.string(),
});

const mwAccountGroupSchema = z.object({
  accountGroup: z.string(),
  id: z.string(),
  mwEntity: z.string(),
});

const catalogSchema = z.object({
  code: z.string(),
  id: z.string(),
  name: z.string(),
  printName: z.string().optional(),
});

const materialAlterCode = z.object({
  id: z.string(),
  isActive: z.boolean(),
  isFromManager: z.boolean(),
  boid: z.string().optional(),
  alterCode: alterCode,
  measurementUnit: z.object({ code: z.string(), id: z.string(), name: z.string() }),
  measurementUnitId: z.string().optional(),
  deleted: z.boolean().optional(),
  alterCodeId: z.string().optional(),
  isDefault: z.boolean().optional(),
  quantityTotalizer: z.boolean().optional(),
  relationFactor: z.number().optional(),
  minSalesQuantity: z.number().optional(),
  shortCode: z.string().optional(),
  lotStart: z.string().optional(),
  lotFinsh: z.string().optional(),
  dimension1: z.number().optional(),
  dimension2: z.number().optional(),
  dimension3: z.number().optional(),
  materialId: z.string().optional(),
});

const competitorMaterials = z.object({
  id: z.string(),
  isActive: z.boolean(),
  isFromManager: z.boolean(),
  isDefault: z.boolean(),
  materialAlterCode: materialAlterCode,
  competitor: z.object({ id: z.string(), code: z.string() }),
  competitorId: z.string().optional(),
  materialId: z.string().optional(),
});

export const material = baseObjectSchema.extend({
  companyId: z.string().optional(),
  code: z.string(),
  name: z.string(),
  materialTypeId: z.string().optional(),
  vatCategoryId: z.string().optional(),
  mwAccountGroupId: z.string().optional(),
  colorCatalogId: z.string().optional(),
  styleCatalogId: z.string().optional(),
  sizeCatalogId: z.string().optional(),
  motherCodeId: z.string().optional(),
  smallCode: z.string().optional(),
  smallName: z.string().optional(),
  sequence: z.number().optional(),
  securityStock: z.number().optional(),
  remarks: z.string().optional(),
  rowStatus: z.nativeEnum(RecordMode).optional(),
  materialType: materialTypeSchema.optional(),
  company: companySchema.optional(),
  vatCategory: vatCategorySchema.optional(),
  mwAccountGroup: mwAccountGroupSchema.optional(),
  colorCatalog: catalogSchema.optional(),
  sizeCatalog: catalogSchema.optional(),
  styleCatalog: catalogSchema.optional(),
  createUser: userSchema.optional(),
  updateUser: userSchema.optional(),
  materialAlterCodes: z.array(materialAlterCode).optional(),
  competitorMaterials: z.array(competitorMaterials).optional(),
  image: z.string().optional(),
  allowZeroValue: z.boolean().optional(),
  allowManualPrice: z.boolean().optional(),
});

export const getMaterialByIdSchema = material;

export type GetMaterialByIdResponse = z.infer<typeof getMaterialByIdSchema>;
